import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  SETTINGS_COLLAPSE_STORAGE_KEY,
  SETTINGS_NAV_GROUPS,
  SETTINGS_NAV_ITEMS,
  type SettingsNavTier,
} from './settingsNav';

interface Props {
  isAdmin: boolean;
  accountOnly?: boolean;
  allowedSectionIds?: string[] | null;
}

function loadCollapsedTiers(): Set<SettingsNavTier> {
  try {
    const raw = sessionStorage.getItem(SETTINGS_COLLAPSE_STORAGE_KEY);
    if (raw) {
      return new Set(JSON.parse(raw) as SettingsNavTier[]);
    }
  } catch {
    /* ignore */
  }
  return new Set(
    SETTINGS_NAV_GROUPS.filter(g => g.defaultCollapsed).map(g => g.tier),
  );
}

const SettingsSidebar: React.FC<Props> = ({ isAdmin, accountOnly = false, allowedSectionIds = null }) => {
  const [collapsedTiers, setCollapsedTiers] = useState<Set<SettingsNavTier>>(loadCollapsedTiers);

  const visibleItems = useMemo(
    () => SETTINGS_NAV_ITEMS.filter(item => {
      if (allowedSectionIds) return allowedSectionIds.includes(item.id);
      if (accountOnly) return item.id === 'account';
      return !item.adminOnly || isAdmin;
    }),
    [accountOnly, allowedSectionIds, isAdmin],
  );

  const toggleTier = useCallback((tier: SettingsNavTier) => {
    setCollapsedTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier);
      else next.add(tier);
      sessionStorage.setItem(SETTINGS_COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  return (
    <aside className="settings-sidebar ops-sidebar" aria-label="Settings navigation">
      <div className="settings-sidebar-header ops-sidebar-header">
        <span className="ops-sidebar-label">Settings</span>
      </div>
      <nav className="settings-sidebar-nav">
        {SETTINGS_NAV_GROUPS.map(group => {
          const items = visibleItems.filter(i => i.tier === group.tier);
          if (items.length === 0) return null;

          const collapsed = group.collapsible && collapsedTiers.has(group.tier);

          return (
            <div key={group.tier} className="settings-nav-group">
              {group.collapsible ? (
                <button
                  type="button"
                  className="settings-nav-group-toggle"
                  onClick={() => toggleTier(group.tier)}
                  aria-expanded={!collapsed}
                >
                  {collapsed ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                  <span>{group.label}</span>
                </button>
              ) : (
                <div className="settings-nav-group-label">{group.label}</div>
              )}
              {!collapsed && (
                <ul className="settings-nav-list">
                  {items.map(item => {
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <NavLink
                          to={`/settings/${item.id}`}
                          className={({ isActive }) =>
                            `settings-nav-item ops-sidebar-item${isActive ? ' active' : ''}`
                          }
                        >
                          <Icon size={16} aria-hidden />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default SettingsSidebar;
